import {useDisclosure} from '@mantine/hooks';
import React, {useCallback, useImperativeHandle, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {AntibodyEnforcedSchemaRole} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {getConflictingFieldNames} from 'antibodies/schema-configuration/AntibodyEnforcedSchemaSpecUtils';
import {
  AntibodyComponentSchemaInterfaceId,
  ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE,
  ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import AntibodyComponentMigrationPreviewModal from 'antibodies/schema-configuration/schema-migration/AntibodyComponentMigrationPreviewModal';
import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import hasEnforcedDataModelChanges from 'antibodies/schema-configuration/schema-migration/hasEnforcedDataModelChanges';
import {assert} from 'Asserts';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';
import {NamingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

export interface AntibodyComponentMigrationControllerHandle {
  open: (spec: AntibodyEnforcedSchemaSpec, interfaceId: AntibodyComponentSchemaInterfaceId) => void;
}

export interface AntibodyComponentMigrationControllerProps {
  registryId: string;
  schemaId: string;
  existingFields: Array<SchemaField | ClientSchemaField>;
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  hasNameTemplate: boolean;
  folderItemType: string | null;
  onMigrationRun: (
    antibodyRole: AntibodyEnforcedSchemaRole,
    preferredNamingStrategies?: Array<NamingStrategy>,
    fieldMigrationMappings?: Array<FieldMigrationMapping>
  ) => Promise<{asyncMigrationPending: boolean}>;
}

const ASYNC_MIGRATION_PENDING_TOAST_MESSAGE =
  "Validation and migration are running in the background. This can take up to several hours — we'll email you when it's ready to review and publish.";

/**
 * Encapsulates the migration preview modal and its open/pending state so that
 * TagSchemaFileSettings (a large class component) can trigger the flow with a
 * single imperative `ref.current.open(spec, interfaceId)` call rather than
 * managing modal state itself.
 */
const AntibodyComponentMigrationController = React.forwardRef<
  AntibodyComponentMigrationControllerHandle,
  AntibodyComponentMigrationControllerProps
>(
  (
    {
      registryId,
      schemaId,
      existingFields,
      existingConstraint,
      existingNamingStrategies,
      hasNameTemplate,
      folderItemType,
      onMigrationRun,
    },
    ref
  ) => {
    const [opened, {open: openDisclosure, close}] = useDisclosure(false);
    const [pendingSpec, setPendingSpec] = useState<AntibodyEnforcedSchemaSpec | null>(null);
    const [pendingInterfaceId, setPendingInterfaceId] = useState<AntibodyComponentSchemaInterfaceId | null>(
      null
    );
    const [isApplying, setIsApplying] = useState(false);
    // Synchronous in-flight guard. The no-impact fast path in `open` fires a migration without opening
    // the modal, and neither `isApplying` state nor the parent's schema value updates until that async
    // work settles — so a second `open` call in the meantime would start a concurrent migration for the
    // same schema. A ref flips synchronously, letting `open` reject re-entrant calls immediately.
    const isMigrationInFlight = useRef(false);

    // Runs the migration and closes the modal on success. Shared between the preview modal's Apply
    // button and the no-impact fast path in `open`, which assigns the component type directly without
    // ever showing the modal (`close` is a harmless no-op when nothing is open).
    const runMigration = useCallback(
      async (
        interfaceId: AntibodyComponentSchemaInterfaceId,
        options: {
          namingStrategiesToAdd?: Array<NamingStrategy>;
          fieldMigrationMappings?: Array<FieldMigrationMapping>;
        }
      ): Promise<void> => {
        const antibodyRole = ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE[interfaceId];
        isMigrationInFlight.current = true;
        setIsApplying(true);
        try {
          const {asyncMigrationPending} = await onMigrationRun(
            antibodyRole,
            options.namingStrategiesToAdd,
            options.fieldMigrationMappings
          );
          close();
          if (asyncMigrationPending) {
            notifications.info(ASYNC_MIGRATION_PENDING_TOAST_MESSAGE, {duration: 0});
          }
          // eslint-disable-next-line benchlint/always-throw-from-catch -- errors are surfaced via toast; swallowing here prevents an uncaught rejection while keeping the modal open for retry
        } catch (e) {
          notifications.notifyError(e);
        } finally {
          isMigrationInFlight.current = false;
          setIsApplying(false);
        }
      },
      [close, onMigrationRun]
    );

    useImperativeHandle(ref, () => ({
      open(spec, interfaceId) {
        // Ignore re-entrant calls while a migration (modal or no-impact fast path) is still running:
        // the parent value and `activeMigration` don't update until it settles, so acting on a stale
        // selection would start a concurrent migration for the same schema.
        if (isMigrationInFlight.current) {
          return;
        }
        const {displayNameConflicts, systemNameConflicts} = getConflictingFieldNames(spec, existingFields);
        if (displayNameConflicts.length > 0) {
          notifications.error(
            `This schema has fields whose names conflict with built-in fields for this protein component type: ${displayNameConflicts.join(', ')}. Please rename these fields.`
          );
        }
        if (systemNameConflicts.length > 0) {
          notifications.error(
            `This schema has fields whose system names conflict with built-in fields for this protein component type: ${systemNameConflicts.join(', ')}. Please rename these fields.`
          );
        }
        if (displayNameConflicts.length > 0 || systemNameConflicts.length > 0) {
          // Field-name conflicts block the migration from starting, so record it rather than
          // silently emitting nothing.
          analytics.trackEventWithModule(
            ANTIBODIES_ANALYTICS_ACTIONS.SCHEMA_MIGRATION_BLOCKED_BY_FIELD_CONFLICT,
            ANALYTICS_MODULE.ANTIBODIES,
            {
              target_protein_component_type: ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE[interfaceId],
              schema_id: schemaId,
            }
          );
          return;
        }
        // When assigning the component type has no effect on the schema (no new fields, constraint, or
        // labeling strategies to add), skip the confirmation modal and assign the type directly.
        if (!hasEnforcedDataModelChanges(spec, existingNamingStrategies)) {
          // runMigration surfaces its own errors via toast and never rejects, so this is safe to
          // fire-and-forget from this synchronous imperative handler.
          runMigration(interfaceId, {}).catch(() => {});
          return;
        }
        setPendingSpec(spec);
        setPendingInterfaceId(interfaceId);
        analytics.trackEventWithModule(
          ANTIBODIES_ANALYTICS_ACTIONS.OPENED_SCHEMA_MIGRATION_PREVIEW_MODAL,
          ANALYTICS_MODULE.ANTIBODIES,
          {
            target_protein_component_type: ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE[interfaceId],
            schema_id: schemaId,
          }
        );
        openDisclosure();
      },
    }));

    const handleApply = useCallback(
      async (options: {
        namingStrategiesToAdd?: Array<NamingStrategy>;
        fieldMigrationMappings: Array<FieldMigrationMapping>;
      }): Promise<void> => {
        assert(pendingInterfaceId != null, 'Expected pendingInterfaceId when applying migration');

        const antibodyRole = ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE[pendingInterfaceId];
        // Each mapping is one existing field the user finalized onto a new enforced field; emit one
        // event per finalized mapping so we can measure how often (and for which component types)
        // users reuse existing fields during a migration.
        options.fieldMigrationMappings.forEach((mapping) => {
          const sourceField = existingFields.find((f) => f.apiId === mapping.sourceFieldId);
          analytics.trackEventWithModule(
            ANTIBODIES_ANALYTICS_ACTIONS.MAPPED_FIELD_FOR_SCHEMA_MIGRATION,
            ANALYTICS_MODULE.ANTIBODIES,
            {
              target_protein_component_type: antibodyRole,
              schema_id: schemaId,
              source_field_type: sourceField?.fieldType ?? null,
              destination_field_name: mapping.destinationFieldName,
            }
          );
        });
        await runMigration(pendingInterfaceId, options);
      },
      [existingFields, pendingInterfaceId, runMigration, schemaId]
    );

    if (!opened || pendingSpec == null || pendingInterfaceId == null) {
      return null;
    }

    const componentTypeName = ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[pendingInterfaceId];
    return (
      <AntibodyComponentMigrationPreviewModal
        componentTypeName={componentTypeName}
        componentInterfaceId={pendingInterfaceId}
        enforcedSchemaSpec={pendingSpec}
        existingFields={existingFields}
        existingConstraint={existingConstraint}
        existingNamingStrategies={existingNamingStrategies}
        hasNameTemplate={hasNameTemplate}
        folderItemType={folderItemType}
        registryId={registryId}
        onClose={close}
        onApply={handleApply}
        isApplying={isApplying}
      />
    );
  }
);

export default AntibodyComponentMigrationController;
